import { Link } from "react-router-dom";
import {
  PRIVACY_POLICY_CANONICAL_URL,
  PRIVACY_POLICY_EFFECTIVE_DATE,
  PRIVACY_POLICY_TITLE,
  PRIVACY_SECTIONS,
} from "./content";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <a
        href="#privacy-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-20 focus:bg-white focus:px-3 focus:py-2 focus:rounded-md focus:ring-2 focus:ring-emerald-500"
      >
        Skip to privacy policy
      </a>
      <header className="border-b border-slate-200 bg-white print:border-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/login" className="flex items-center min-w-0">
            <span className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center mr-3 shrink-0" aria-hidden>
              <i className="ri-stack-line text-white" />
            </span>
            <span className="text-base font-semibold text-slate-900 truncate">Tradivo</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-emerald-700 hover:text-emerald-800 print:hidden">
            Sign in
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        <article id="privacy-content">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{PRIVACY_POLICY_TITLE}</h1>
          <p className="mt-3 text-sm text-slate-500" data-testid="privacy-effective-date">
            Effective {PRIVACY_POLICY_EFFECTIVE_DATE}
          </p>
          <p className="mt-2 text-sm text-slate-500" data-testid="privacy-canonical-url">
            Canonical URL: {PRIVACY_POLICY_CANONICAL_URL}
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-700">
            This policy explains how Tradivo handles personal information. It is written for people who use the
            product, people whose information a business stores in the product, and marketplace partners that require a
            public privacy URL.
          </p>

          <nav aria-label="Privacy policy contents" className="mt-8 p-4 sm:p-5 rounded-xl border border-slate-200 bg-white print:break-inside-avoid">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">Contents</h2>
            <ol className="columns-1 sm:columns-2 gap-x-8 text-sm">
              {PRIVACY_SECTIONS.map((section, i) => (
                <li key={section.id} className="mb-1.5 break-inside-avoid">
                  <a className="text-emerald-800 hover:underline" href={`#${section.id}`}>
                    {i + 1}. {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {PRIVACY_SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="mt-10 scroll-mt-6">
              <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>
              {section.blocks.map((block, idx) => {
                if (block.type === "p") {
                  return (
                    <p key={idx} className="mt-3 text-sm sm:text-base leading-relaxed text-slate-700">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === "ul") {
                  return (
                    <ul key={idx} className="mt-3 list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700">
                      {block.items.map((item) => (
                        <li key={item.slice(0, 48)}>{item}</li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === "email") {
                  return (
                    <p key={idx} className="mt-3 text-sm sm:text-base leading-relaxed text-slate-700">
                      Email:{" "}
                      <a
                        className="text-emerald-800 hover:underline"
                        href={`mailto:${block.email}`}
                        data-testid="privacy-contact-email"
                      >
                        {block.email}
                      </a>
                    </p>
                  );
                }
                if (block.type === "address") {
                  return (
                    <address
                      key={idx}
                      className="mt-3 not-italic text-sm sm:text-base leading-relaxed text-slate-700"
                      data-testid="privacy-postal-address"
                    >
                      {block.lines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </address>
                  );
                }
                return (
                  <p
                    key={idx}
                    className="mt-3 text-sm sm:text-base leading-relaxed text-slate-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5"
                    role="note"
                  >
                    {block.text}
                  </p>
                );
              })}
            </section>
          ))}
        </article>

        <footer className="mt-14 pt-6 border-t border-slate-200 text-sm text-slate-500 print:mt-8">
          <p>Tradivo · Privacy Policy · {PRIVACY_POLICY_CANONICAL_URL} · Effective {PRIVACY_POLICY_EFFECTIVE_DATE}</p>
          <p className="mt-2 print:hidden">
            <Link className="text-emerald-700 hover:underline" to="/login">
              Return to sign in
            </Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
